import React from 'react';
import { MessageSquareIcon, CheckSquareIcon, GitBranchIcon, FileIcon, SearchIcon, ArrowRightIcon, NetworkIcon } from 'lucide-react';
export function Comparison() {
  return <section className="py-32 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-white mb-6">
            One graph to coordinate them all
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Stop context-switching between a dozen tools. StreamGraph unifies
            your workflow.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-center pt-6 overflow-visible">
          {/* Before */}
          <div className="relative p-8 rounded-2xl bg-surface-800 border border-surface-600 overflow-visible">
            <div className="absolute -top-4 left-8 z-10">
              <span className="px-4 py-1.5 bg-surface-900 text-danger-400 text-sm font-medium rounded-full border border-danger-500/50">
                Before
              </span>
            </div>

            <div className="h-64 flex items-center justify-center">
              <div className="relative">
                {/* Scattered tool icons */}
                <div className="absolute -top-8 -left-12 w-12 h-12 bg-surface-700 rounded-xl flex items-center justify-center border border-surface-500 animate-float" style={{
                animationDelay: '0s'
              }}>
                  <MessageSquareIcon className="w-6 h-6 text-gray-400" />
                </div>
                <div className="absolute -top-4 right-0 w-12 h-12 bg-surface-700 rounded-xl flex items-center justify-center border border-surface-500 animate-float" style={{
                animationDelay: '0.5s'
              }}>
                  <CheckSquareIcon className="w-6 h-6 text-gray-400" />
                </div>
                <div className="absolute top-8 -left-8 w-12 h-12 bg-surface-700 rounded-xl flex items-center justify-center border border-surface-500 animate-float" style={{
                animationDelay: '1s'
              }}>
                  <GitBranchIcon className="w-6 h-6 text-gray-400" />
                </div>
                <div className="absolute top-12 right-4 w-12 h-12 bg-surface-700 rounded-xl flex items-center justify-center border border-surface-500 animate-float" style={{
                animationDelay: '1.5s'
              }}>
                  <FileIcon className="w-6 h-6 text-gray-400" />
                </div>
                <div className="absolute bottom-0 left-4 w-12 h-12 bg-surface-700 rounded-xl flex items-center justify-center border border-surface-500 animate-float" style={{
                animationDelay: '2s'
              }}>
                  <SearchIcon className="w-6 h-6 text-gray-400" />
                </div>

                {/* Chaos lines */}
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 200">
                  <path d="M40 60 Q 100 40 140 80" stroke="rgba(239, 68, 68, 0.3)" strokeWidth="2" fill="none" strokeDasharray="4 4" />
                  <path d="M60 100 Q 80 140 160 120" stroke="rgba(239, 68, 68, 0.3)" strokeWidth="2" fill="none" strokeDasharray="4 4" />
                  <path d="M80 40 Q 120 100 100 160" stroke="rgba(239, 68, 68, 0.3)" strokeWidth="2" fill="none" strokeDasharray="4 4" />
                </svg>
              </div>
            </div>

            <p className="text-center text-gray-400 mt-4">
              Slack, Jira, GitHub, Notion, Docs...
              <br />
              <span className="text-danger-400">
                Context scattered everywhere
              </span>
            </p>
          </div>

          {/* Arrow */}
          <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 z-10">
            <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center shadow-lg shadow-primary-500/50">
              <ArrowRightIcon className="w-6 h-6 text-white" />
            </div>
          </div>

          {/* After */}
          <div className="relative p-8 rounded-2xl bg-surface-700 border border-primary-500/50 shadow-lg shadow-primary-500/10 overflow-visible">
            <div className="absolute -top-4 left-8 z-10">
              <span className="px-4 py-1.5 bg-surface-900 text-primary-400 text-sm font-medium rounded-full border border-primary-500/50">
                After
              </span>
            </div>

            <div className="h-64 flex items-center justify-center">
              <div className="relative">
                {/* Unified graph visualization */}
                <div className="w-24 h-24 rounded-2xl bg-primary-500/20 border border-primary-500/50 flex items-center justify-center">
                  <NetworkIcon className="w-12 h-12 text-primary-400" />
                </div>

                {/* Connected nodes */}
                <div className="absolute -top-4 -left-16 w-10 h-10 bg-success-500/20 rounded-lg flex items-center justify-center border border-success-500/30">
                  <div className="w-3 h-3 bg-success-400 rounded-full" />
                </div>
                <div className="absolute -top-4 -right-16 w-10 h-10 bg-primary-500/20 rounded-lg flex items-center justify-center border border-primary-500/30">
                  <div className="w-3 h-3 bg-primary-400 rounded-full animate-pulse" />
                </div>
                <div className="absolute -bottom-4 -left-12 w-10 h-10 bg-success-500/20 rounded-lg flex items-center justify-center border border-success-500/30">
                  <div className="w-3 h-3 bg-success-400 rounded-full" />
                </div>
                <div className="absolute -bottom-4 -right-12 w-10 h-10 bg-surface-600 rounded-lg flex items-center justify-center border border-surface-500">
                  <div className="w-3 h-3 bg-gray-400 rounded-full" />
                </div>

                {/* Connection lines */}
                <svg className="absolute inset-0 w-full h-full" viewBox="-80 -40 200 160" style={{
                overflow: 'visible'
              }}>
                  <line x1="-40" y1="0" x2="0" y2="40" stroke="rgba(16, 185, 129, 0.5)" strokeWidth="2" />
                  <line x1="100" y1="0" x2="80" y2="40" stroke="rgba(59, 130, 246, 0.5)" strokeWidth="2" />
                  <line x1="-20" y1="100" x2="20" y2="80" stroke="rgba(16, 185, 129, 0.5)" strokeWidth="2" />
                  <line x1="100" y1="100" x2="80" y2="80" stroke="rgba(107, 114, 128, 0.5)" strokeWidth="2" />
                </svg>
              </div>
            </div>

            <p className="text-center text-gray-400 mt-4">
              One unified graph
              <br />
              <span className="text-primary-400">
                Everything connected, nothing lost
              </span>
            </p>
          </div>
        </div>
      </div>
    </section>;
}